import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, Snackbar, Alert, AlertTitle } from "@mui/material";
import axios from "../../utils/api";
import { getClientEmail } from "../../utils/getClientEmail";
import {
  getEmployeeIdFromToken,
  parseEmployeeIdFromAdminDetails,
} from "../../utils/tokenClaims";

const TicketForm = () => {
  const [userInfo, setUserInfo] = useState({
    employeeId: "",
    loaded: false,
  });
  // Set current date as default
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };
  const [date, setDate] = useState(getCurrentDate());
  const [entries, setEntries] = useState([
    { ticketNo: "", noOfHours: "", description: "", work: "" },
  ]);
  const [ticketNumbers, setTicketNumbers] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    const from = location.state?.from;
    if (typeof from === "string" && from.startsWith("/")) {
      navigate(from);
      return;
    }
    navigate(-1);
  };

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (index, e) => {
    const values = [...entries];
    values[index][e.target.name] = e.target.value;
    if (e.target.name === "ticketNo") {
      const ticketNos = values.map((entry) => entry.ticketNo);
      const duplicates = ticketNos.filter(
        (item, idx) => ticketNos.indexOf(item) !== idx
      );
      if (duplicates.length > 0) {
        setSnackbar({
          open: true,
          message: "Ticket numbers must be unique!",
          severity: "error",
        });
        values[index][e.target.name] = ""; // Clear the conflicting input
      }
    }
    setEntries(values);
  };

  const handleAddEntry = () => {
    setEntries([
      ...entries,
      { ticketNo: "", noOfHours: "", description: "", work: "" },
    ]);
  };

  const handleDeleteEntry = (index) => {
    const values = [...entries];
    const deletedTicketNo = values[index].ticketNo;
    values.splice(index, 1);

    // Remove the deleted ticket number from the set
    const newTicketNumbers = new Set(ticketNumbers);
    newTicketNumbers.delete(deletedTicketNo);
    setTicketNumbers(newTicketNumbers);

    setEntries(values);
  };

  // Resolve employee ID from JWT claims, then admin profile API
  useEffect(() => {
    let cancelled = false;

    const loadUserInfo = async () => {
      const token = getAccessToken();
      if (!token) {
        if (!cancelled) setUserInfo({ employeeId: "", loaded: true });
        return;
      }

      let employeeId = getEmployeeIdFromToken(token);
      const email = getClientEmail();

      if (!employeeId && email) {
        try {
          const res = await axios.get(
            `getemail-admin-details/${encodeURIComponent(email)}`
          );
          employeeId = parseEmployeeIdFromAdminDetails(res.data);
        } catch (error) {
          console.error("Error fetching employee ID:", error);
        }
      }

      if (!cancelled) {
        setUserInfo({ employeeId, loaded: true });
        if (!employeeId) {
          setSnackbar({
            open: true,
            message: "Employee ID not found for your account",
            severity: "warning",
          });
        }
      }
    };

    loadUserInfo();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate date
    if (!date) {
      setSnackbar({
        open: true,
        message: "Please select a date",
        severity: "error",
      });
      return;
    }
    // Check for duplicate ticket numbers
    const ticketNos = entries.map((entry) => entry.ticketNo);
    const uniqueTicketNos = new Set(ticketNos);
    if (ticketNos.length !== uniqueTicketNos.size) {
      setSnackbar({
        open: true,
        message: "Ticket numbers must be unique!",
        severity: "error",
      });
      return;
    }
    // Validate all fields are filled
    for (let entry of entries) {
      if (
        !entry.ticketNo ||
        !entry.noOfHours ||
        !entry.description ||
        !entry.work
      ) {
        setSnackbar({
          open: true,
          message:
            "Please fill all fields for every entry, including work type",
          severity: "error",
        });
        return;
      }
      if (!["AI", "Robot"].includes(entry.work)) {
        setSnackbar({
          open: true,
          message: "Work type must be 'AI' or 'Robot'",
          severity: "error",
        });
        return;
      }
    }
    const data = {
      date,
      entries,
    };
    try {
      await axios.post("/ticketsdata", data);
      setSnackbar({
        open: true,
        message: "Time entries submitted successfully!",
        severity: "success",
      });
      // Reset form after successful submission
      setDate(getCurrentDate()); // Reset to current date
      setEntries([
        { ticketNo: "", noOfHours: "", description: "", work: "AI" },
      ]);
      setTicketNumbers(new Set());
      // Navigate after a delay to let user see the success message
      setTimeout(() => navigate("/EmployeeList"), 1500);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error submitting data",
        severity: "error",
      });
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "2.5rem 0 3rem",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
      }}
    >
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <AlertTitle>
            {snackbar.severity.charAt(0).toUpperCase() +
              snackbar.severity.slice(1)}
          </AlertTitle>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Back Button - Fixed in top left corner of page */}
      <IconButton
        onClick={handleBack}
        style={{
          position: "absolute",
          top: "24px",
          left: "24px",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 1000,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div
        style={{
          maxWidth: "820px",
          margin: "0 auto",
          padding: "0 1.25rem",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h1
            style={{
              margin: 0,
              marginBottom: "0.5rem",
              fontSize: "2rem",
              fontWeight: "700",
              letterSpacing: "-0.5px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Time Entry Form
          </h1>
          <p
            style={{
              margin: 0,
              color: "#64748b",
              fontSize: "0.95rem",
              fontWeight: "400",
            }}
          >
            Log your ticket hours and work type
          </p>
        </div>

        <div
          style={{
            backgroundColor: "white",
            borderRadius: "14px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
            padding: "2.25rem 2rem",
            marginBottom: "2rem",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "2rem",
              flexWrap: "wrap",
              gap: "1.25rem",
            }}
          >
            <div>
              <p
                style={{
                  color: "#64748b",
                  margin: "0 0 0.35rem 0",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Employee ID
              </p>
              <p
                style={{
                  color: "#1e293b",
                  margin: 0,
                  fontSize: "1.125rem",
                  fontWeight: "600",
                }}
              >
                {!userInfo.loaded
                  ? "Loading..."
                  : userInfo.employeeId || "Not available"}
              </p>
            </div>

            <div style={{ minWidth: "160px" }}>
              <label
                htmlFor="date"
                style={{
                  display: "block",
                  color: "#64748b",
                  marginBottom: "0.35rem",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "0.6rem 0.75rem",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3498db";
                  e.target.style.boxShadow = "0 0 0 3px rgba(52,152,219,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {entries.map((entry, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "1.75rem",
                  marginBottom: "1.75rem",
                  backgroundColor: index % 2 === 0 ? "#fafbfc" : "#f8fafc",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.25rem",
                  }}
                >
                  <h3
                    style={{
                      color: "#1e293b",
                      margin: 0,
                      fontSize: "1.1rem",
                      fontWeight: "600",
                    }}
                  >
                    Entry - {index + 1}
                  </h3>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleDeleteEntry(index)}
                      style={{
                        backgroundColor: "transparent",
                        color: "#dc2626",
                        border: "none",
                        padding: "0.35rem 0.6rem",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#fef2f2";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "1.5rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <label
                      htmlFor={`ticketNo-${index}`}
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      Ticket Number
                    </label>
                    <input
                      type="text"
                      id={`ticketNo-${index}`}
                      name="ticketNo"
                      value={entry.ticketNo}
                      onChange={(e) => handleInputChange(index, e)}
                      required
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "0.7rem 0.85rem",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "0.95rem",
                        outline: "none",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#3498db";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(52,152,219,0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label
                      htmlFor={`noOfHours-${index}`}
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      Hours Worked
                    </label>
                    <input
                      type="number"
                      id={`noOfHours-${index}`}
                      name="noOfHours"
                      value={entry.noOfHours}
                      onChange={(e) => {
                        const value = Math.max(0, Number(e.target.value));
                        handleInputChange(index, {
                          target: { name: "noOfHours", value },
                        });
                      }}
                      min="0"
                      step="0.5"
                      required
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "0.7rem 0.85rem",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "0.95rem",
                        outline: "none",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#3498db";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(52,152,219,0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <label
                      htmlFor={`work-${index}`}
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: "#64748b",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      Work Type
                    </label>
                    <select
                      id={`work-${index}`}
                      name="work"
                      value={entry.work}
                      onChange={(e) => handleInputChange(index, e)}
                      required
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "0.7rem 2.25rem 0.7rem 0.85rem",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "0.95rem",
                        appearance: "none",
                        background:
                          'url(\'data:image/svg+xml;utf8,<svg fill="%2364748b" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>\') no-repeat right 0.75rem center',
                        backgroundColor: "white",
                        backgroundSize: "1rem",
                        outline: "none",
                        cursor: "pointer",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#3498db";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(52,152,219,0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e2e8f0";
                        e.target.style.boxShadow = "none";
                      }}
                    >
                      <option value="" disabled>
                        Select Work Type
                      </option>
                      <option value="AI">AI</option>
                      <option value="Robot">Robot</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`description-${index}`}
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: "#64748b",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    Description
                  </label>
                  <textarea
                    id={`description-${index}`}
                    name="description"
                    value={entry.description}
                    onChange={(e) => handleInputChange(index, e)}
                    required
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: "0.7rem 0.85rem",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      minHeight: "88px",
                      fontSize: "0.95rem",
                      resize: "vertical",
                      outline: "none",
                      transition: "border-color 0.2s, box-shadow 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#3498db";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(52,152,219,0.15)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "none";
                    }}
                  ></textarea>
                </div>
              </div>
            ))}

            <div
              style={{
                display: "flex",
                gap: "1rem",
                marginTop: "1.5rem",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={handleAddEntry}
                style={{
                  backgroundColor: "white",
                  color: "#3498db",
                  border: "1px solid #3498db",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  transition: "background-color 0.2s, color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f0f7ff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                }}
              >
                + Add Another Entry
              </button>

              <button
                type="submit"
                style={{
                  backgroundColor: "#3498db",
                  color: "white",
                  border: "none",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                  flex: 1,
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#2980b9";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#3498db";
                }}
              >
                Submit Time Entries
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketForm;
